import React, { useEffect, useState } from "react";
import { Plus, Trash2, Copy, ExternalLink, MessagesSquare, Inbox, CheckCircle2 } from "lucide-react";
import { Switch } from "../../../components/ui/switch";
import { api } from "../../../lib/api";
import { inp, card, btn, btnMain, copyText, trackLink, Label, Empty } from "./shared";

const Stat = ({ I, n, l, testid }) => (
  <div className="flex items-center gap-2 rounded-xl bg-[#f4f8f7] px-3 py-2" data-testid={testid}>
    <I size={15} className="text-[#0e8c7f]" /><span className="text-[16px] font-bold text-[#0f2e2a]">{n ?? 0}</span><span className="text-[12px] text-[#6b7c79]">{l}</span>
  </div>
);

const PageCard = ({ p, st, update, remove }) => {
  const f = (k) => ({ value: p[k] ?? "", onChange: (e) => update(p.id, { [k]: e.target.value }, false), onBlur: (e) => update(p.id, { [k]: e.target.value }) });
  return (
    <div className={card} data-testid={`fb-page-${p.slug}`}>
      <div className="mb-3 flex flex-wrap items-center gap-3">
        <input {...f("name")} className={`${inp} max-w-xs font-semibold`} data-testid={`fb-page-name-${p.slug}`} />
        <label className="flex items-center gap-2 text-[13px] text-[#3d504d]">
          <Switch checked={!!p.active} onCheckedChange={(v) => update(p.id, { active: v })} data-testid={`fb-page-active-${p.slug}`} />Active in schedule
        </label>
        <button onClick={() => remove(p.id)} className={`${btn} ml-auto`} data-testid={`fb-page-delete-${p.slug}`}><Trash2 size={14} />Delete</button>
      </div>
      <div className="mb-3 flex flex-wrap gap-2">
        <Stat I={MessagesSquare} n={st?.chats} l="chats" testid={`fb-page-chats-${p.slug}`} />
        <Stat I={Inbox} n={st?.quotes} l="quotes" testid={`fb-page-quotes-${p.slug}`} />
        <Stat I={CheckCircle2} n={st?.posted} l="posts done" testid={`fb-page-posted-${p.slug}`} />
      </div>
      <div className="grid gap-3 md:grid-cols-2">
        <Label t="Facebook page link"><input {...f("url")} className={inp} placeholder="https://facebook.com/yourpage" data-testid={`fb-page-url-${p.slug}`} /></Label>
        <Label t="Short code (used in the chat link)"><input {...f("slug")} className={inp} data-testid={`fb-page-slug-${p.slug}`} /></Label>
        <Label t="Video post time"><input type="time" {...f("post_time")} className={inp} data-testid={`fb-page-posttime-${p.slug}`} /></Label>
        <Label t="Picture post time"><input type="time" {...f("picture_time")} className={inp} data-testid={`fb-page-pictime-${p.slug}`} /></Label>
        <Label t="Notes" className="md:col-span-2"><textarea rows={2} {...f("notes")} className={inp} data-testid={`fb-page-notes-${p.slug}`} /></Label>
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-2 rounded-xl bg-[#e8f8f4] px-3 py-2 text-[13px]">
        <span className="font-semibold text-[#0b6f65]">Chat link:</span>
        <code className="break-all text-[#0f2e2a]" data-testid={`fb-page-link-${p.slug}`}>{trackLink(p)}</code>
        <button onClick={() => copyText(trackLink(p), "Chat link copied")} className={btn} data-testid={`fb-page-copy-link-${p.slug}`}><Copy size={13} />Copy</button>
        {p.url && <a href={p.url} target="_blank" rel="noreferrer" className={btn} data-testid={`fb-page-open-${p.slug}`}><ExternalLink size={13} />Open page</a>}
      </div>
    </div>
  );
};

export default function PagesSection({ store }) {
  const { items, update, remove, add } = store;
  const [stats, setStats] = useState({});
  const [name, setName] = useState("");
  useEffect(() => { api.get("/admin/fb/pages/stats").then(({ data }) => setStats(data)).catch(() => {}); }, [items.length]);
  const create = async () => {
    if (!name.trim()) return;
    await add({ name: name.trim() });
    setName("");
  };
  return (
    <div className="space-y-4" data-testid="fb-pages-section">
      <div className={`${card} flex flex-wrap items-end gap-3`}>
        <Label t="New page name" className="min-w-[220px] flex-1"><input value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && create()} className={inp} placeholder="e.g. StripWise Texas" data-testid="fb-page-new-name" /></Label>
        <button onClick={create} className={btnMain} data-testid="fb-page-add"><Plus size={15} />Add page</button>
      </div>
      {items.length === 0 ? <Empty testid="fb-pages-empty">No pages yet. Add your first Facebook page above.</Empty>
        : items.map((p) => <PageCard key={p.id} p={p} st={stats[p.id]} update={update} remove={remove} />)}
    </div>
  );
}
